import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  Chip,
  Button,
  IconButton,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Snackbar,
  CircularProgress,
  Stepper,
  Step,
  StepLabel,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  LocalShipping as TruckIcon,
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Schedule as ScheduleIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Home as HomeIcon,
  Cancel as CancelIcon,
  PlayArrow as StartIcon,
  Done as DoneIcon,
  Report as ReportIcon,
  LocationOn as LocationIcon,
  Refresh as RefreshIcon,
} from '@mui/icons-material';
import {
  getDelivery,
  getDeliveryHistory,
  acceptDelivery,
  startDelivery,
  arriveDelivery,
  completeDelivery,
  reportDeliveryProblem,
  cancelDelivery,
} from '../api';

// Статусы с метаданными
const DELIVERY_STATUSES = {
  pending: { label: 'Ожидает', color: 'default', icon: <ScheduleIcon />, step: 0 },
  ready: { label: 'Готов', color: 'info', icon: <TruckIcon />, step: 1 },
  assigned: { label: 'Назначен', color: 'primary', icon: <PersonIcon />, step: 2 },
  accepted: { label: 'Принят', color: 'secondary', icon: <CheckIcon />, step: 3 },
  in_transit: { label: 'В пути', color: 'warning', icon: <TruckIcon />, step: 4 },
  arrived: { label: 'На месте', color: 'info', icon: <HomeIcon />, step: 5 },
  delivered: { label: 'Доставлен', color: 'success', icon: <CheckIcon />, step: 6 },
  problem: { label: 'Проблема', color: 'error', icon: <WarningIcon />, step: -1 },
  cancelled: { label: 'Отменён', color: 'default', icon: <CancelIcon />, step: -1 },
};

const PROBLEM_TYPES = {
  no_one_home: 'Никого нет дома',
  wrong_address: 'Неверный адрес',
  customer_refused: 'Клиент отказался',
  damaged: 'Товар повреждён',
  cannot_fit: 'Не помещается',
  other: 'Другое',
};

// Диалог проблемы
function ProblemDialog({ open, onClose, onSubmit }) {
  const [problemType, setProblemType] = useState('');
  const [description, setDescription] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!problemType) return;
    setLoading(true);
    try {
      await onSubmit({ problem_type: problemType, description });
      onClose();
      setProblemType('');
      setDescription('');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { m: { xs: 1, sm: 4 }, width: { xs: 'calc(100% - 16px)', sm: '100%' } } }}>
      <DialogTitle>Сообщить о проблеме</DialogTitle>
      <DialogContent>
        <FormControl fullWidth sx={{ mt: 2 }}>
          <InputLabel>Тип проблемы</InputLabel>
          <Select
            value={problemType}
            label="Тип проблемы"
            onChange={(e) => setProblemType(e.target.value)}
          >
            {Object.entries(PROBLEM_TYPES).map(([key, label]) => (
              <MenuItem key={key} value={key}>{label}</MenuItem>
            ))}
          </Select>
        </FormControl>
        <TextField
          fullWidth
          multiline
          rows={3}
          label="Описание"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          sx={{ mt: 2 }}
        />
      </DialogContent>
      <DialogActions sx={{ flexWrap: 'wrap' }}>
        <Button onClick={onClose}>Отмена</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="error"
          disabled={!problemType || loading}
          startIcon={loading ? <CircularProgress size={20} /> : <ReportIcon />}
        >
          Сообщить
        </Button>
      </DialogActions>
    </Dialog>
  );
}

// Диалог завершения
function CompleteDialog({ open, onClose, onSubmit }) {
  const [recipientName, setRecipientName] = useState('');
  const [notes, setNotes] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!recipientName.trim()) {
      alert('Укажите имя того, кто принял доставку');
      return;
    }
    setLoading(true);
    try {
      await onSubmit({ recipient_name: recipientName.trim(), notes });
      onClose();
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth PaperProps={{ sx: { m: { xs: 1, sm: 4 }, width: { xs: 'calc(100% - 16px)', sm: '100%' } } }}>
      <DialogTitle>Подтвердить доставку</DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          required
          error={!recipientName.trim()}
          label="Кто принял"
          value={recipientName}
          onChange={(e) => setRecipientName(e.target.value)}
          sx={{ mt: 2 }}
        />
        <TextField
          fullWidth
          multiline
          rows={3}
          label="Примечание"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          sx={{ mt: 2 }}
        />
      </DialogContent>
      <DialogActions sx={{ flexWrap: 'wrap' }}>
        <Button onClick={onClose}>Отмена</Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          color="success"
          disabled={loading || !recipientName.trim()}
          startIcon={loading ? <CircularProgress size={20} /> : <DoneIcon />}
        >
          Подтвердить
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default function DeliveryDetails({ user }) {
  const { deliveryId } = useParams();
  const navigate = useNavigate();
  const [delivery, setDelivery] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [problemDialogOpen, setProblemDialogOpen] = useState(false);
  const [completeDialogOpen, setCompleteDialogOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const isAdmin = user?.role === 'admin' || user?.role === 'manager';
  const isDriver = user?.role === 'driver';
  const isMyDelivery = delivery?.driver_id === user?.id;

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [deliveryRes, historyRes] = await Promise.all([
        getDelivery(deliveryId),
        getDeliveryHistory(deliveryId).catch(() => ({ data: [] })),
      ]);
      setDelivery(deliveryRes.data);
      setHistory(historyRes.data);
    } catch (error) {
      console.error('Error loading delivery:', error);
      setSnackbar({ open: true, message: 'Ошибка загрузки', severity: 'error' });
    } finally {
      setLoading(false);
    }
  }, [deliveryId]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleAction = async (action, data) => {
    setActionLoading(true);
    try {
      switch (action) {
        case 'accept':
          await acceptDelivery(deliveryId);
          break;
        case 'start':
          await startDelivery(deliveryId);
          break;
        case 'arrive':
          await arriveDelivery(deliveryId);
          break;
        case 'complete':
          if (delivery?.status !== 'arrived') throw new Error('Delivery must be arrived before completion');
          await completeDelivery(deliveryId, data);
          break;
        case 'problem':
          await reportDeliveryProblem(deliveryId, data);
          break;
        case 'cancel':
          await cancelDelivery(deliveryId);
          break;
        default:
          break;
      }
      setSnackbar({ open: true, message: 'Статус обновлён', severity: 'success' });
      loadData();
    } catch (error) {
      console.error('Error updating delivery:', error);
      setSnackbar({ open: true, message: 'Ошибка обновления', severity: 'error' });
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusInfo = (status) => {
    return DELIVERY_STATUSES[status] || { label: status, color: 'default' };
  };

  const getActiveStep = () => {
    if (!delivery) return 0;
    const statusInfo = DELIVERY_STATUSES[delivery.status];
    return statusInfo?.step >= 0 ? statusInfo.step : 0;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!delivery) {
    return (
      <Box>
        <Alert severity="error">Доставка не найдена</Alert>
        <Button startIcon={<BackIcon />} onClick={() => navigate('/deliveries')} sx={{ mt: 2 }}>
          Назад к списку
        </Button>
      </Box>
    );
  }

  const statusInfo = getStatusInfo(delivery.status);

  return (
    <Box>
      {/* Заголовок */}
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', mb: 3, gap: { xs: 1, sm: 2 } }}>
        <IconButton aria-label="Назад к списку доставок" onClick={() => navigate('/deliveries')}>
          <BackIcon />
        </IconButton>
        <Typography variant="h4" sx={{ flex: '1 1 200px', fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
          Доставка #{delivery.id}
        </Typography>
        <Chip
          icon={statusInfo.icon}
          label={statusInfo.label}
          color={statusInfo.color}
          size="large"
        />
        <IconButton aria-label="Обновить доставку" onClick={loadData}>
          <RefreshIcon />
        </IconButton>
      </Box>

      {/* Прогресс */}
      {delivery.status !== 'cancelled' && delivery.status !== 'problem' && (
        <Paper sx={{ p: 2, mb: 3, overflowX: 'auto' }}>
          <Stepper activeStep={getActiveStep()} alternativeLabel sx={{ minWidth: 680 }}>
            <Step><StepLabel>Создана</StepLabel></Step>
            <Step><StepLabel>Готова</StepLabel></Step>
            <Step><StepLabel>Назначена</StepLabel></Step>
            <Step><StepLabel>Принята</StepLabel></Step>
            <Step><StepLabel>В пути</StepLabel></Step>
            <Step><StepLabel>На месте</StepLabel></Step>
            <Step><StepLabel>Доставлена</StepLabel></Step>
          </Stepper>
        </Paper>
      )}

      <Grid container spacing={3}>
        {/* Основная информация */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              <HomeIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Адрес доставки
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography variant="body1" sx={{ fontWeight: 'bold' }}>
                  {delivery.address}
                </Typography>
                {delivery.city && (
                  <Typography variant="body2" color="text.secondary">
                    {delivery.city}
                  </Typography>
                )}
              </Grid>
              
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary">Подъезд</Typography>
                <Typography variant="body1">{delivery.entrance || '—'}</Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary">Этаж</Typography>
                <Typography variant="body1">{delivery.floor || '—'}</Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary">Квартира</Typography>
                <Typography variant="body1">{delivery.apartment || '—'}</Typography>
              </Grid>
              <Grid item xs={6} sm={3}>
                <Typography variant="caption" color="text.secondary">Лифт</Typography>
                <Typography variant="body1">{delivery.has_elevator ? 'Есть' : 'Нет'}</Typography>
              </Grid>
            </Grid>
          </Paper>

          <Paper sx={{ p: 3, mb: 3 }}>
            <Typography variant="h6" gutterBottom>
              <PersonIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Контактная информация
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">Имя клиента</Typography>
                <Typography variant="body1">{delivery.customer_name || '—'}</Typography>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Typography variant="caption" color="text.secondary">Телефон</Typography>
                <Typography variant="body1">
                  <PhoneIcon fontSize="small" sx={{ mr: 0.5, verticalAlign: 'middle' }} />
                  {delivery.customer_phone || delivery.phone ? (
                    <a href={`tel:${delivery.customer_phone || delivery.phone}`}>{delivery.customer_phone || delivery.phone}</a>
                  ) : '—'}
                </Typography>
              </Grid>
              {(delivery.customer_phone2 || delivery.additional_phone) && (
                <Grid item xs={12} sm={6}>
                  <Typography variant="caption" color="text.secondary">Доп. телефон</Typography>
                  <Typography variant="body1">
                    <a href={`tel:${delivery.customer_phone2 || delivery.additional_phone}`}>{delivery.customer_phone2 || delivery.additional_phone}</a>
                  </Typography>
                </Grid>
              )}
            </Grid>
          </Paper>

          {/* История статусов */}
          {history.length > 0 && (
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                📜 История изменений
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              {history.map((item, index) => (
                <Box key={item.id} sx={{ display: 'flex', mb: 2 }}>
                  <Box sx={{ 
                    width: 8, 
                    height: 8, 
                    borderRadius: '50%', 
                    backgroundColor: 'primary.main',
                    mt: 1,
                    mr: 2
                  }} />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2">
                      <strong>{getStatusInfo(item.old_status)?.label || 'Создано'}</strong>
                      {' → '}
                      <strong>{getStatusInfo(item.new_status)?.label}</strong>
                    </Typography>
                    {item.notes && (
                      <Typography variant="body2" color="text.secondary">
                        {item.notes}
                      </Typography>
                    )}
                    <Typography variant="caption" color="text.secondary">
                      {new Date(item.created_at).toLocaleString('ru-RU')}
                    </Typography>
                  </Box>
                </Box>
              ))}
            </Paper>
          )}
        </Grid>

        {/* Боковая панель */}
        <Grid item xs={12} md={4}>
          {/* Информация о диване */}
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>🛋️ Диван</Typography>
              <Typography variant="body1">
                #{delivery.sofa_number || delivery.sofa_id}
              </Typography>
              {delivery.sofa_model && (
                <Typography variant="body2" color="text.secondary">
                  {delivery.sofa_model}
                </Typography>
              )}
            </CardContent>
          </Card>

          {/* Водитель */}
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>🚗 Водитель</Typography>
              {delivery.driver_name ? (
                <Typography variant="body1">{delivery.driver_name}</Typography>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Не назначен
                </Typography>
              )}
            </CardContent>
          </Card>

          {/* Дата и время */}
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>📅 Дата доставки</Typography>
              {delivery.scheduled_date ? (
                <>
                  <Typography variant="body1">
                    {delivery.scheduled_date.slice(0, 10).split('-').reverse().join('.')}
                  </Typography>
                  {delivery.time_slot_start && (
                    <Typography variant="body2" color="text.secondary">
                      {delivery.time_slot_start} - {delivery.time_slot_end || '...'}
                    </Typography>
                  )}
                </>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  Не назначена
                </Typography>
              )}
            </CardContent>
          </Card>

          {/* Оплата */}
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>💰 Оплата</Typography>
              <Grid container spacing={1}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Подъём</Typography>
                  <Typography variant="body1">{delivery.lift_payment || 0} ₸</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="text.secondary">Сборка</Typography>
                  <Typography variant="body1">{delivery.assembly_payment || 0} ₸</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Divider sx={{ my: 1 }} />
                  <Typography variant="caption" color="text.secondary">Итого</Typography>
                  <Typography variant="h6">
                    {(delivery.lift_payment || 0) + (delivery.assembly_payment || 0)} ₸
                  </Typography>
                  {delivery.is_paid && (
                    <Chip label="Оплачено" color="success" size="small" sx={{ mt: 1 }} />
                  )}
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          {/* Действия */}
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>⚡ Действия</Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                
                {/* Действия водителя */}
                {isDriver && isMyDelivery && (
                  <>
                    {delivery.status === 'assigned' && (
                      <Button
                        variant="contained"
                        color="primary"
                        startIcon={<CheckIcon />}
                        onClick={() => handleAction('accept')}
                        disabled={actionLoading}
                      >
                        Принять
                      </Button>
                    )}
                    
                    {delivery.status === 'accepted' && (
                      <Button
                        variant="contained"
                        color="warning"
                        startIcon={<StartIcon />}
                        onClick={() => handleAction('start')}
                        disabled={actionLoading}
                      >
                        Выехал
                      </Button>
                    )}
                    
                    {delivery.status === 'in_transit' && (
                      <Button
                        variant="contained"
                        color="info"
                        startIcon={<LocationIcon />}
                        onClick={() => handleAction('arrive')}
                        disabled={actionLoading}
                      >
                        Прибыл
                      </Button>
                    )}
                    
                    {delivery.status === 'arrived' && (
                      <Button
                        variant="contained"
                        color="success"
                        startIcon={<DoneIcon />}
                        onClick={() => setCompleteDialogOpen(true)}
                        disabled={actionLoading}
                      >
                        Завершить
                      </Button>
                    )}
                    
                    {!['delivered', 'cancelled'].includes(delivery.status) && (
                      <Button
                        variant="outlined"
                        color="error"
                        startIcon={<ReportIcon />}
                        onClick={() => setProblemDialogOpen(true)}
                        disabled={actionLoading}
                      >
                        Проблема
                      </Button>
                    )}
                  </>
                )}

                {/* Действия админа */}
                {isAdmin && (
                  <>
                    {!['delivered', 'cancelled'].includes(delivery.status) && (
                      <Button
                        variant="outlined"
                        color="error"
                        startIcon={<CancelIcon />}
                        onClick={() => handleAction('cancel')}
                        disabled={actionLoading}
                      >
                        Отменить
                      </Button>
                    )}
                  </>
                )}

                {actionLoading && (
                  <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                    <CircularProgress size={24} />
                  </Box>
                )}
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Комментарий */}
      {delivery.comment && (
        <Paper sx={{ p: 3, mt: 3 }}>
          <Typography variant="h6" gutterBottom>💬 Комментарий</Typography>
          <Typography variant="body1">{delivery.comment}</Typography>
        </Paper>
      )}

      {/* Диалоги */}
      <ProblemDialog
        open={problemDialogOpen}
        onClose={() => setProblemDialogOpen(false)}
        onSubmit={(data) => handleAction('problem', data)}
      />

      <CompleteDialog
        open={completeDialogOpen}
        onClose={() => setCompleteDialogOpen(false)}
        onSubmit={(data) => handleAction('complete', data)}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert severity={snackbar.severity} onClose={() => setSnackbar({ ...snackbar, open: false })}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
